import React, { useEffect, useState } from "react";
import axios from "axios";

const TarixceQ = () => {
    const [allData, setAllData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [search, setSearch] = useState("");

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retrying, setRetrying] = useState(false);

    const [filters, setFilters] = useState({
        earnedAboveZero: null,
        invitedAboveZero: null,
    });

    const [expandedRow, setExpandedRow] = useState(null);

    const toggleRow = (index) => {
        setExpandedRow((prev) => (prev === index ? null : index));
    };

    /**
     * Serverdən gələn xətanı istifadəçiyə
     * mümkün qədər ətraflı göstərir.
     */
    const getErrorMessage = (error, defaultMessage) => {
        if (!error) {
            return defaultMessage;
        }

        const responseData = error.response?.data;

        if (responseData) {
            if (typeof responseData === "string" && responseData.trim()) {
                return responseData;
            }

            if (responseData.message) {
                let message = responseData.message;

                if (responseData.details) {
                    message += `\nƏlavə məlumat: ${responseData.details}`;
                }

                if (
                    responseData.error &&
                    responseData.error !== responseData.message
                ) {
                    message += `\nTexniki məlumat: ${responseData.error}`;
                }

                return message;
            }

            if (responseData.error) {
                return `Xəta: ${responseData.error}`;
            }

            if (responseData.details) {
                return `Əlavə məlumat: ${responseData.details}`;
            }

            try {
                const serverResponse = JSON.stringify(responseData);

                if (serverResponse && serverResponse !== "{}") {
                    return `Serverin cavabı: ${serverResponse}`;
                }
            } catch (stringifyError) {
                console.error(
                    "Server xətası oxunarkən problem yarandı:",
                    stringifyError
                );
            }
        }

        if (error.response?.status) {
            const status = error.response.status;

            if (status === 400) {
                return "Sorğuda düzgün olmayan məlumat göndərildi.";
            }

            if (status === 401) {
                return "Sessiyanızın müddəti bitib və ya giriş icazəniz yoxdur. Zəhmət olmasa hesabınıza yenidən daxil olun.";
            }

            if (status === 403) {
                return "Bu məlumatlara baxmaq üçün icazəniz yoxdur.";
            }

            if (status === 404) {
                return "Tələb olunan məlumat və ya server ünvanı tapılmadı.";
            }

            if (status === 500) {
                return "Server daxilində xəta baş verdi. Server cavabında əlavə məlumat yoxdursa, bir qədər sonra yenidən cəhd edin.";
            }

            return `Server ${status} kodu ilə cavab verdi.`;
        }

        if (error.request) {
            return "Serverə sorğu göndərildi, lakin cavab alınmadı. İnternet bağlantınızı və serverin işlək vəziyyətdə olduğunu yoxlayın.";
        }

        if (error.message) {
            return `Sorğu zamanı xəta baş verdi: ${error.message}`;
        }

        return defaultMessage;
    };

    /**
     * Məbləğlərin səliqəli göstərilməsi.
     */
    const formatMoney = (value) => {
        const number = Number(value);

        if (!Number.isFinite(number)) {
            return "0.00";
        }

        return number.toLocaleString("az-AZ", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    /**
     * Bütün məlumatların serverdən alınması.
     */
    const fetchData = async (isRetry = false) => {
        try {
            if (isRetry) {
                setRetrying(true);
            } else {
                setLoading(true);
            }

            setError(null);

            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/historyQOne`,
                {
                    withCredentials: true,
                }
            );

            const responseData = Array.isArray(response.data)
                ? response.data
                : [];

            setAllData(responseData);
            setFilteredData(responseData);
            setExpandedRow(null);
        } catch (error) {
            console.error("Tarixçə qazanc məlumatları alınarkən xəta:", error);

            setError(
                getErrorMessage(
                    error,
                    "Tarixçə qazanc məlumatlarını yükləmək mümkün olmadı."
                )
            );

            setAllData([]);
            setFilteredData([]);
        } finally {
            setLoading(false);
            setRetrying(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    /**
     * Axtarış və filtrlərin tətbiqi.
     */
    useEffect(() => {
        let tempData = [...allData];

        const searchLower = search.trim().toLowerCase();

        if (searchLower) {
            tempData = tempData.filter((item) => {
                const name = String(item?.referrerName || "").toLowerCase();
                const referralCode = String(
                    item?.referrerReferralCode || ""
                ).toLowerCase();

                return (
                    name.includes(searchLower) ||
                    referralCode.includes(searchLower)
                );
            });
        }

        if (filters.earnedAboveZero !== null) {
            tempData = tempData.filter((item) => {
                const totalEarned = Number(item?.totalEarned) || 0;

                return filters.earnedAboveZero
                    ? totalEarned > 0
                    : totalEarned <= 0;
            });
        }

        if (filters.invitedAboveZero !== null) {
            tempData = tempData.filter((item) => {
                const totalInvited = Number(item?.totalInvited) || 0;

                return filters.invitedAboveZero
                    ? totalInvited > 0
                    : totalInvited <= 0;
            });
        }

        setFilteredData(tempData);
    }, [search, filters, allData]);

    const toggleFilter = (key, value) => {
        setFilters((prev) => ({
            ...prev,
            [key]: prev[key] === value ? null : value,
        }));
    };

    const handleSuggestionClick = (referrerName) => {
        setSearch(referrerName);
    };

    const clearSearch = () => {
        setSearch("");
    };

    const clearFilters = () => {
        setFilters({
            earnedAboveZero: null,
            invitedAboveZero: null,
        });
    };

    const filterButton = (label, key, value) => (
        <button
            type="button"
            onClick={() => toggleFilter(key, value)}
            className={`px-4 py-2.5 rounded-xl text-sm font-semibold border transition-all duration-200 ${
                filters[key] === value
                    ? "bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-500/20"
                    : "bg-white text-gray-700 border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-700"
            }`}
        >
            {label}
        </button>
    );

    const activeFilterCount = Object.values(filters).filter(
        (value) => value !== null
    ).length;

    return (
        <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-gray-100 to-indigo-50 dark:from-gray-950 dark:via-gray-900 dark:to-slate-950 px-4 sm:px-6 py-8 sm:py-12 transition-colors">
            <div className="max-w-7xl mx-auto">

                {/* Başlıq */}
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 p-6 sm:p-8 mb-8 shadow-2xl">
                    <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
                    <div className="absolute -left-20 -bottom-24 w-72 h-72 rounded-full bg-white/10 blur-3xl" />

                    <div className="relative">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white/15 border border-white/20 backdrop-blur-md flex items-center justify-center text-2xl">
                                🎯
                            </div>

                            <div>
                                <p className="text-indigo-100 text-sm font-medium">
                                    Qazanc tarixçəsi
                                </p>

                                <h1 className="text-2xl sm:text-4xl font-extrabold text-white">
                                    Tarixçə Qazanc Məlumatları
                                </h1>
                            </div>
                        </div>

                        <p className="relative mt-4 text-indigo-100 text-sm sm:text-base max-w-3xl">
                            Dəvət fəaliyyətiniz üzrə qazanc, dəvət olunan istifadəçilər
                            və dövrlər üzrə qazanc məlumatlarına baxın.
                        </p>
                    </div>
                </div>

                {/* Yüklənmə vəziyyəti */}
                {loading && (
                    <div className="mb-8 rounded-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 shadow-xl p-8">
                        <div className="flex flex-col items-center justify-center text-center">
                            <div className="w-14 h-14 rounded-full border-4 border-indigo-100 dark:border-gray-700 border-t-indigo-600 animate-spin mb-5" />

                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                                Qazanc məlumatları yüklənir...
                            </h2>

                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                Məlumatlar serverdən alınır. Zəhmət olmasa gözləyin.
                            </p>
                        </div>
                    </div>
                )}

                {/* Xəta */}
                {error && !loading && (
                    <div className="mb-8 rounded-3xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 p-5 sm:p-6 shadow-lg">
                        <div className="flex flex-col lg:flex-row gap-5 lg:items-start lg:justify-between">
                            <div className="flex gap-4">
                                <div className="shrink-0 w-12 h-12 rounded-2xl bg-red-100 dark:bg-red-900/40 flex items-center justify-center text-xl">
                                    ⚠️
                                </div>

                                <div>
                                    <h2 className="text-lg font-bold text-red-800 dark:text-red-300">
                                        Məlumatları yükləmək mümkün olmadı
                                    </h2>

                                    <p className="mt-2 text-sm leading-6 text-red-700 dark:text-red-300 whitespace-pre-line">
                                        {error}
                                    </p>

                                    <p className="mt-3 text-xs text-red-600/80 dark:text-red-400">
                                        Yuxarıdakı məlumat xətanın serverdən gələn
                                        açıqlamasını və ya bağlantı probleminin səbəbini göstərir.
                                    </p>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => fetchData(true)}
                                disabled={retrying}
                                className="shrink-0 px-5 py-3 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold transition shadow-md"
                            >
                                {retrying
                                    ? "Yenidən yoxlanılır..."
                                    : "Yenidən cəhd et"}
                            </button>
                        </div>
                    </div>
                )}

                {/* Əsas məzmun */}
                {!loading && !error && (
                    <>
                        {/* Məlumat yoxdur */}
                        {allData.length === 0 ? (
                            <div className="rounded-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 shadow-xl p-10 text-center">
                                <div className="w-16 h-16 mx-auto rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-3xl mb-5">
                                    📭
                                </div>

                                <h2 className="text-xl font-bold">
                                    Qazanc məlumatı tapılmadı
                                </h2>

                                <p className="mt-2 text-gray-500 dark:text-gray-400">
                                    Hazırda göstərilə biləcək tarixçə qazanc məlumatı yoxdur.
                                </p>

                                <button
                                    type="button"
                                    onClick={() => fetchData(true)}
                                    disabled={retrying}
                                    className="mt-6 px-5 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-semibold transition"
                                >
                                    {retrying
                                        ? "Məlumatlar yenilənir..."
                                        : "Məlumatları yenilə"}
                                </button>
                            </div>
                        ) : (
                            <>
                                {/* Axtarış */}
                                <div className="rounded-3xl bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border border-gray-200 dark:border-white/10 shadow-xl p-5 sm:p-6 mb-6">
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">
                                        🔍 İstifadəçi axtarışı
                                    </label>

                                    <div className="relative max-w-2xl">
                                        <input
                                            type="text"
                                            placeholder="İstifadəçi adı və ya referans kodu ilə axtar..."
                                            value={search}
                                            onChange={(e) =>
                                                setSearch(e.target.value)
                                            }
                                            className="w-full p-4 pr-12 text-sm rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                                        />

                                        {search && (
                                            <button
                                                type="button"
                                                onClick={clearSearch}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-xl bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-200 transition"
                                                aria-label="Axtarışı təmizlə"
                                            >
                                                ✕
                                            </button>
                                        )}

                                        {/* Axtarış təklifləri */}
                                        {search.trim() && (
                                            <div className="absolute left-0 right-0 top-full mt-2 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl z-50 max-h-72 overflow-y-auto">
                                                {allData
                                                    .filter((item) => {
                                                        const name = String(
                                                            item?.referrerName || ""
                                                        ).toLowerCase();

                                                        const referralCode = String(
                                                            item?.referrerReferralCode || ""
                                                        ).toLowerCase();

                                                        const query = search
                                                            .trim()
                                                            .toLowerCase();

                                                        return (
                                                            name.includes(query) ||
                                                            referralCode.includes(query)
                                                        );
                                                    })
                                                    .slice(0, 10)
                                                    .map((item, idx) => (
                                                        <button
                                                            type="button"
                                                            key={idx}
                                                            onClick={() =>
                                                                handleSuggestionClick(
                                                                    item?.referrerName || ""
                                                                )
                                                            }
                                                            className="w-full text-left px-4 py-3 hover:bg-indigo-50 dark:hover:bg-gray-700 border-b last:border-b-0 border-gray-100 dark:border-gray-700 transition"
                                                        >
                                                            <div className="font-semibold text-gray-900 dark:text-white">
                                                                {item?.referrerName ||
                                                                    "Ad göstərilməyib"}
                                                            </div>

                                                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                                {item?.referrerEmail ||
                                                                    "E-poçt göstərilməyib"}
                                                            </div>
                                                        </button>
                                                    ))}

                                                {allData.filter((item) => {
                                                    const name = String(
                                                        item?.referrerName || ""
                                                    ).toLowerCase();

                                                    const referralCode = String(
                                                        item?.referrerReferralCode || ""
                                                    ).toLowerCase();

                                                    const query = search
                                                        .trim()
                                                        .toLowerCase();

                                                    return (
                                                        name.includes(query) ||
                                                        referralCode.includes(query)
                                                    );
                                                }).length === 0 && (
                                                    <div className="p-5 text-center text-sm text-gray-500 dark:text-gray-400">
                                                        Uyğun istifadəçi tapılmadı.
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Filtrlər */}
                                <div className="rounded-3xl bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border border-gray-200 dark:border-white/10 shadow-xl p-5 sm:p-6 mb-6">
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                                        <div>
                                            <h2 className="text-lg font-bold">
                                                Filtrlər
                                            </h2>

                                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                Qazanc və dəvət vəziyyətinə görə nəticələri süzün.
                                            </p>
                                        </div>

                                        {activeFilterCount > 0 && (
                                            <button
                                                type="button"
                                                onClick={clearFilters}
                                                className="px-4 py-2 rounded-xl text-sm font-semibold bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition"
                                            >
                                                Filtrləri sıfırla
                                            </button>
                                        )}
                                    </div>

                                    <div className="flex flex-wrap gap-3">
                                        {filterButton(
                                            "✅ Qazancı olanlar",
                                            "earnedAboveZero",
                                            true
                                        )}

                                        {filterButton(
                                            "❌ Qazancı olmayanlar",
                                            "earnedAboveZero",
                                            false
                                        )}

                                        {filterButton(
                                            "👥 Dəvət edənlər",
                                            "invitedAboveZero",
                                            true
                                        )}

                                        {filterButton(
                                            "🙅 Dəvət etməyənlər",
                                            "invitedAboveZero",
                                            false
                                        )}
                                    </div>

                                    <div className="mt-5 flex flex-wrap gap-3">
                                        <div className="px-4 py-2 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 text-sm font-semibold">
                                            Ümumi istifadəçi: {allData.length}
                                        </div>

                                        <div className="px-4 py-2 rounded-xl bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300 text-sm font-semibold">
                                            Göstərilən nəticə: {filteredData.length}
                                        </div>
                                    </div>
                                </div>

                                {/* Cədvəl */}
                                <div className="rounded-3xl border border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl shadow-2xl overflow-hidden">
                                    <div className="p-5 sm:p-6 border-b border-gray-200 dark:border-gray-800">
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                            <div>
                                                <h2 className="text-xl sm:text-2xl font-extrabold">
                                                    📋 Qazanc hesabatı
                                                </h2>

                                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                    İstifadəçilərin qazanc və dəvət göstəriciləri
                                                </p>
                                            </div>

                                            {search && (
                                                <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                                                    Axtarış: "{search}"
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <div className="max-h-[600px] overflow-y-auto">
                                            <table className="min-w-[1050px] w-full">
                                                <thead className="sticky top-0 z-20 backdrop-blur-md bg-white/95 dark:bg-gray-900/95 text-gray-700 dark:text-gray-200 shadow-md">
                                                    <tr>
                                                        <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                                            #
                                                        </th>

                                                        <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                                            İstifadəçi
                                                        </th>

                                                        <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                                            E-poçt
                                                        </th>

                                                        <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                                            Ümumi qazanc
                                                        </th>

                                                        <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                                            Dəvət olunanlar
                                                        </th>

                                                        <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                                            Ödəniş edənlər
                                                        </th>

                                                        <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                                                            Dövr üzrə qazanc
                                                        </th>
                                                    </tr>
                                                </thead>

                                                <tbody className="text-gray-800 dark:text-gray-100">
                                                    {filteredData.map((user, i) => {
                                                        const isExpanded =
                                                            expandedRow === i;

                                                        return (
                                                            <React.Fragment
                                                                key={`${user?.referrerReferralCode || "user"}-${i}`}
                                                            >
                                                                <tr
                                                                    onClick={() =>
                                                                        toggleRow(i)
                                                                    }
                                                                    className="cursor-pointer border-t border-gray-200 dark:border-gray-700 hover:bg-indigo-50/70 dark:hover:bg-gray-800/80 transition-all duration-200"
                                                                >
                                                                    <td className="px-6 py-5 font-bold text-gray-500 dark:text-gray-400">
                                                                        {i + 1}
                                                                    </td>

                                                                    <td className="px-6 py-5">
                                                                        <div className="font-semibold">
                                                                            {user?.referrerName ||
                                                                                "Ad göstərilməyib"}
                                                                        </div>

                                                                        {user?.referrerReferralCode && (
                                                                            <div className="text-xs text-gray-400 mt-1">
                                                                                Referans kodu:{" "}
                                                                                {
                                                                                    user.referrerReferralCode
                                                                                }
                                                                            </div>
                                                                        )}
                                                                    </td>

                                                                    <td className="px-6 py-5 text-gray-600 dark:text-gray-300">
                                                                        {user?.referrerEmail ||
                                                                            "E-poçt göstərilməyib"}
                                                                    </td>

                                                                    <td className="px-6 py-5">
                                                                        <span className="inline-flex px-3 py-1.5 rounded-xl bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300 font-bold">
                                                                            {formatMoney(
                                                                                user?.totalEarned
                                                                            )}{" "}
                                                                            AZN
                                                                        </span>
                                                                    </td>

                                                                    <td className="px-6 py-5 font-semibold">
                                                                        {user?.count ?? 0}
                                                                    </td>

                                                                    <td className="px-6 py-5 font-semibold">
                                                                        {user?.totalInvited ?? 0}
                                                                    </td>

                                                                    <td className="px-6 py-5">
                                                                        <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 font-semibold">
                                                                            {isExpanded
                                                                                ? "▼ Gizlət"
                                                                                : "▶ Göstər"}
                                                                        </span>
                                                                    </td>
                                                                </tr>

                                                                {isExpanded && (
                                                                    <tr className="bg-gray-50 dark:bg-gray-800/80">
                                                                        <td
                                                                            colSpan="7"
                                                                            className="px-6 py-5"
                                                                        >
                                                                            <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-5">
                                                                                <div className="flex items-center justify-between gap-4 mb-4">
                                                                                    <h3 className="font-bold text-lg">
                                                                                        📅 Dövrlər üzrə qazanc
                                                                                    </h3>

                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={(event) => {
                                                                                            event.stopPropagation();
                                                                                            setExpandedRow(
                                                                                                null
                                                                                            );
                                                                                        }}
                                                                                        className="px-3 py-2 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-sm font-semibold transition"
                                                                                    >
                                                                                        Gizlət
                                                                                    </button>
                                                                                </div>

                                                                                {Array.isArray(
                                                                                    user?.periodEarnings
                                                                                ) &&
                                                                                user.periodEarnings
                                                                                    .length > 0 ? (
                                                                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                                                                        {user.periodEarnings.map(
                                                                                            (
                                                                                                period,
                                                                                                index
                                                                                            ) => (
                                                                                                <div
                                                                                                    key={
                                                                                                        index
                                                                                                    }
                                                                                                    className="rounded-2xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4"
                                                                                                >
                                                                                                    <div className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                                                                                                        {
                                                                                                            period?.periodLabel
                                                                                                        }
                                                                                                    </div>

                                                                                                    <div className="mt-2 text-lg font-extrabold text-green-600 dark:text-green-400">
                                                                                                        {formatMoney(
                                                                                                            period?.earned
                                                                                                        )}{" "}
                                                                                                        AZN
                                                                                                    </div>
                                                                                                </div>
                                                                                            )
                                                                                        )}
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="text-center py-6 text-sm text-gray-500 dark:text-gray-400">
                                                                                        Bu istifadəçi üzrə dövr məlumatı tapılmadı.
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Nəticə yoxdur */}
                                    {filteredData.length === 0 && (
                                        <div className="border-t border-gray-200 dark:border-gray-800 p-10 text-center">
                                            <div className="w-16 h-16 mx-auto rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-3xl mb-5">
                                                🔎
                                            </div>

                                            <h3 className="text-xl font-bold">
                                                Uyğun məlumat tapılmadı
                                            </h3>

                                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                                                Axtarış sözünü dəyişin və ya aktiv filtrləri
                                                sıfırlayaraq yenidən cəhd edin.
                                            </p>

                                            {(search || activeFilterCount > 0) && (
                                                <div className="flex justify-center gap-3 mt-6 flex-wrap">
                                                    {search && (
                                                        <button
                                                            type="button"
                                                            onClick={clearSearch}
                                                            className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold transition"
                                                        >
                                                            Axtarışı təmizlə
                                                        </button>
                                                    )}

                                                    {activeFilterCount > 0 && (
                                                        <button
                                                            type="button"
                                                            onClick={clearFilters}
                                                            className="px-5 py-2.5 rounded-xl bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-semibold transition"
                                                        >
                                                            Filtrləri sıfırla
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default TarixceQ;
